/**
 * The Settings page's tabs, and the channel tabs inside its Notifications tab.
 *
 * Both live in the query string (`/dashboard/settings?tab=notifications&channel=sms`)
 * rather than in component state, so a reload keeps the reader where they were
 * and a link elsewhere in the dashboard — the SMS centre's "nothing is switched
 * on" notice — can open the exact switch it is talking about.
 *
 * The first entry of each list is the default and is left out of the URL, so the
 * plain `/dashboard/settings` stays the address of the plain page.
 */
export const SETTINGS_PATH = "/dashboard/settings";

export const SettingsTab = {
  ACCOUNT: "account",
  NOTIFICATIONS: "notifications",
  LANGUAGE: "language",
};

export const NotificationChannelTab = {
  EMAIL: "email",
  BROWSER: "browser",
  SMS: "sms",
};

export const SETTINGS_TABS = [
  { id: SettingsTab.ACCOUNT, icon: "bi-person", labelKey: "Dashboard.Settings.tabs.account" },
  { id: SettingsTab.NOTIFICATIONS, icon: "bi-bell", labelKey: "Dashboard.Settings.tabs.notifications" },
  { id: SettingsTab.LANGUAGE, icon: "bi-globe2", labelKey: "Dashboard.Settings.tabs.language" },
];

export const NOTIFICATION_CHANNEL_TABS = [
  { id: NotificationChannelTab.EMAIL, icon: "bi-envelope", labelKey: "Dashboard.Notifications.channels.email" },
  { id: NotificationChannelTab.BROWSER, icon: "bi-bell", labelKey: "Dashboard.Notifications.channels.browser" },
  { id: NotificationChannelTab.SMS, icon: "bi-chat-left-text", labelKey: "Dashboard.Notifications.channels.sms" },
];

/** The requested tab when the list has it, otherwise the list's default. */
export const pickTab = (tabs, requested) =>
  tabs.some((tab) => tab.id === requested) ? requested : tabs[0].id;

/**
 * Writes a tab and channel into `params` — a copy, so the caller can hand it
 * straight to `setSearchParams` — leaving every other parameter (`lng`, say)
 * untouched. Defaults are removed rather than written, and a channel only
 * means something on the Notifications tab.
 */
export const withSettingsLocation = (params, { tab, channel } = {}) => {
  const next = new URLSearchParams(params);
  const put = (key, value, fallback) => {
    if (value && value !== fallback) next.set(key, value);
    else next.delete(key);
  };
  put("tab", tab, SettingsTab.ACCOUNT);
  put(
    "channel",
    tab === SettingsTab.NOTIFICATIONS ? channel : null,
    NotificationChannelTab.EMAIL,
  );
  return next;
};

/** A link to one place on the Settings page. */
export const settingsPath = (location) => {
  const query = withSettingsLocation("", location).toString();
  return query ? `${SETTINGS_PATH}?${query}` : SETTINGS_PATH;
};
